import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { SeasonStateType } from '@redux/types'
import {
	SeasonCreationType,
	SeasonModificationType,
	SeasonProductBatchUpdateType,
	SeasonProductType,
	SeasonType,
	ProductType,
	SeasonProductPhaseType,
	SeasonProductPhaseUpdateType,
} from '@schemas'
import dayjs from 'dayjs'

const initialState: SeasonStateType = {
	seasonList: [],
	openSeason: null,
	queueList: [],
	selectedSeason: null,
	seasonProducts: [],
	products: [],
	selectedSeasonProduct: null,
	seasonProductPhases: [],
	phaseLoading: false,

	selectedYear: dayjs().format('YYYY'),
	loading: false,
	error: null,
	openError: null,

	// 페이지 초기화 관련
	triggerInitSeasonPage: false,
}

const seasonSlice = createSlice({
	name: 'season',
	initialState,
	reducers: {
		// 페이지 초기화 관련
		TRIGGER_INIT_SEASON_PAGE: (state, action: PayloadAction<boolean>) => {
			state.loading = true
			state.triggerInitSeasonPage = action.payload
		},
		TRIGGER_INIT_SEASON_PAGE_SUCCESS: state => {
			state.loading = false
		},
		TRIGGER_INIT_SEASON_PAGE_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 상품 리스트 관련
		SET_PRODUCT_LIST: (state, action: PayloadAction<ProductType[]>) => {
			state.products = action.payload
		},
		// 시즌 리스트 관련
		SEASON_LIST: (state, action: PayloadAction<SeasonType[]>) => {
			state.seasonList = action.payload
		},
		// 연도 필터와 무관하게 유지되는 판매중 시즌
		OPEN_SEASON_INFO: (state, action: PayloadAction<SeasonType | null>) => {
			state.openSeason = action.payload
		},
		// 연도 필터와 무관하게 유지되는 판매 대기열 (queueOrder 오름차순)
		QUEUE_LIST: (state, action: PayloadAction<SeasonType[]>) => {
			state.queueList = action.payload
		},
		SET_SELECTED_SEASON: (state, action: PayloadAction<SeasonType | null>) => {
			state.selectedSeason = action.payload
		},
		SET_SELECTED_YEAR: (state, action: PayloadAction<string>) => {
			state.selectedYear = action.payload
		},
		// 시즌 등록 관련
		REGISTER_SEASON: (state, action: PayloadAction<SeasonCreationType>) => {
			state.loading = true
		},
		REGISTER_SEASON_SUCCESS: state => {
			state.loading = false
		},
		REGISTER_SEASON_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 시즌 수정 관련
		UPDATE_SEASON: (
			state,
			action: PayloadAction<{
				id: string
				season: SeasonModificationType
			}>,
		) => {
			state.loading = true
		},
		UPDATE_SEASON_SUCCESS: state => {
			state.loading = false
		},
		UPDATE_SEASON_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 시즌 삭제 관련
		DELETE_SEASON: (state, action: PayloadAction<string>) => {
			state.loading = true
		},
		DELETE_SEASON_SUCCESS: (state, action: PayloadAction<string>) => {
			state.seasonList = state.seasonList.filter(season => season.id !== action.payload)
			state.loading = false
		},
		DELETE_SEASON_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 판매 시즌 전환 관련
		// 성공 시 목록을 다시 받아오므로 여기서 seasonList 를 직접 건드리지 않는다.
		OPEN_SEASON: (state, action: PayloadAction<string>) => {
			state.loading = true
			state.openError = null
		},
		OPEN_SEASON_SUCCESS: state => {
			state.loading = false
			state.openError = null
		},
		/** 판매 기간 밖(400) 등 전환 실패. 모달에서 사유를 보여주기 위해 별도 필드에 담는다. */
		OPEN_SEASON_FAILURE: (state, action: PayloadAction<string>) => {
			state.openError = action.payload
			state.loading = false
		},
		CLEAR_OPEN_ERROR: state => {
			state.openError = null
		},
		CLOSE_SEASON: state => {
			state.loading = true
		},
		CLOSE_SEASON_SUCCESS: state => {
			state.loading = false
		},
		CLOSE_SEASON_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 판매 대기열 관련
		UPDATE_SEASON_QUEUE: (state, action: PayloadAction<string[]>) => {
			// 드래그 결과를 즉시 반영하고 실패 시 재조회로 되돌린다
			const orderOf = (id: string) => action.payload.indexOf(id)

			state.seasonList = state.seasonList.map(aSeason => {
				const idx = orderOf(aSeason.id)
				return { ...aSeason, queueOrder: idx === -1 ? null : idx }
			})

			// 대기열은 연도 목록 밖에 있으므로 따로 맞춘다. 목록에 없는 시즌(다른 연도)도
			// 이미 queueList 에 들어와 있으니 그대로 순서만 바꾼다.
			const known = new Map(
				[...state.queueList, ...state.seasonList].map(aSeason => [aSeason.id, aSeason]),
			)
			const nextQueue: typeof state.queueList = []
			action.payload.forEach(anId => {
				const aSeason = known.get(anId)
				if (aSeason !== undefined) {
					nextQueue.push({ ...aSeason, queueOrder: nextQueue.length })
				}
			})
			state.queueList = nextQueue
		},
		UPDATE_SEASON_QUEUE_SUCCESS: state => {
			state.loading = false
		},
		UPDATE_SEASON_QUEUE_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.loading = false
		},
		// 시즌 상품 관련
		SET_SEASON_PRODUCT_LIST: (state, action: PayloadAction<SeasonProductType[]>) => {
			state.seasonProducts = action.payload
		},
		FETCH_SEASON_PRODUCT: (state, action: PayloadAction<string>) => {
			// This action will be handled in the saga
		},
		// 시즌 상품 수정 관련
		BATCH_UPDATE_SEASON_PRODUCT: (
			state,
			action: PayloadAction<SeasonProductBatchUpdateType[]>,
		) => {
			// This action will be handled in the saga
		},
		BATCH_UPDATE_SEASON_PRODUCT_SUCCESS: state => {
			// This action will be handled in the saga
		},
		BATCH_UPDATE_SEASON_PRODUCT_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
		},

		// 시즌 상품 상세 (판매·이행 단계) 관련
		SET_SELECTED_SEASON_PRODUCT: (
			state,
			action: PayloadAction<SeasonProductType | null>,
		) => {
			state.selectedSeasonProduct = action.payload
		},
		FETCH_SEASON_PRODUCT_PHASE: (state, action: PayloadAction<string>) => {
			state.phaseLoading = true
		},
		SET_SEASON_PRODUCT_PHASE_LIST: (
			state,
			action: PayloadAction<SeasonProductPhaseType[]>,
		) => {
			state.seasonProductPhases = action.payload
			state.phaseLoading = false
		},
		BATCH_UPDATE_SEASON_PRODUCT_PHASE: (
			state,
			action: PayloadAction<{
				seasonProductId: string
				phaseList: SeasonProductPhaseUpdateType[]
			}>,
		) => {
			state.phaseLoading = true
		},
		BATCH_UPDATE_SEASON_PRODUCT_PHASE_SUCCESS: state => {
			state.phaseLoading = false
		},
		BATCH_UPDATE_SEASON_PRODUCT_PHASE_FAILURE: (state, action: PayloadAction<string>) => {
			state.error = action.payload
			state.phaseLoading = false
		},
	},
})

export const seasonActions = seasonSlice.actions
export default seasonSlice.reducer
